import React, {
  useContext, useState, useEffect, useReducer, useMemo, useCallback,
} from 'react';
import {
  TextField, Grid, Autocomplete, Radio, FormControlLabel,
} from '@mui/material';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import { Button } from '@/shared/components/Button';
import PaxDetailsTable from './PaxDetailsTable';
import { BookingContext } from '../../../../../../context/BookingContext';
import { AuthContext } from '../../../../../../context/AuthContext';
import ShowTripInfo from './ShowTripInfo';

// Constants for action types to avoid typos
const ACTIONS = {
  SET_TOTAL_MEALS: 'SET_TOTAL_MEALS',
  SET_MEAL_TOTAL_RATE: 'SET_MEAL_TOTAL_RATE',
  SET_MEAL_DATE: 'SET_MEAL_DATE',
  SET_PAX_DATA: 'SET_PAX_DATA',
  SET_SELECTED_TRIP: 'SET_SELECTED_TRIP',
  SET_TRIP_OPTION: 'SET_TRIP_OPTION',
  SET_TIMING_OPTION: 'SET_TIMING_OPTION',
  SET_SELECTED_PAX: 'SET_SELECTED_PAX',
  SET_AGENT_OPTION: 'SET_AGENT_OPTION',
  SET_SELECTED_AGENT: 'SET_SELECTED_AGENT',
  SET_TRIP_DATA: 'SET_TRIP_DATA',
  SET_RE_INITIAL_VALUE: 'SET_RE_INITIAL_VALUE',
  SET_DATE: 'SET_DATE',
  SET_FORMAT_OPTION: 'SET_FORMAT_OPTION',
  RESET_FORM: 'RESET_FORM',
  RESET_TIMING: 'RESET_TIMING',
};

const initialState = {
  totalMeals: 0,
  mealTotalRate: 0,
  mealDate: null,
  selectedTrip: null,
  paxData: [],
  tripOption: [],
  agentOption: [],
  selectedAgent: null,
  tripData: {},
  selecttimingOption: null,
  reInitialValue: {},
  selectesPax: null,
  date: {
    startDate: new Date(),
    endDate: new Date(),
  },
};

function bookingReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_TOTAL_MEALS:
      return { ...state, totalMeals: action.payload };
    case ACTIONS.SET_MEAL_TOTAL_RATE:
      return { ...state, mealTotalRate: action.payload };
    case ACTIONS.SET_MEAL_DATE:
      return { ...state, mealDate: action.payload };
    case ACTIONS.SET_PAX_DATA:
      return { ...state, paxData: action.payload };
    case ACTIONS.SET_SELECTED_TRIP:
      return { ...state, selectedTrip: action.payload };
    case ACTIONS.SET_TRIP_OPTION:
      return { ...state, tripOption: action.payload };
    case ACTIONS.SET_TIMING_OPTION:
      return { ...state, selecttimingOption: action.payload };
    case ACTIONS.RESET_TIMING:
      return { ...state, selecttimingOption: null };
    case ACTIONS.SET_SELECTED_PAX:
      return { ...state, selectesPax: action.payload };
    case ACTIONS.SET_AGENT_OPTION:
      return { ...state, agentOption: action.payload };
    case ACTIONS.SET_SELECTED_AGENT:
      return { ...state, selectedAgent: action.payload };
    case ACTIONS.SET_TRIP_DATA:
      return { ...state, tripData: action.payload };
    case ACTIONS.SET_RE_INITIAL_VALUE:
      return {
        ...state,
        reInitialValue: {
          ...state.reInitialValue,
          ...action.payload,
        },
      };
    case ACTIONS.SET_DATE:
      return { ...state, date: action.payload };
      case ACTIONS.RESET_FORM:
        return {
          ...initialState,
          agentOption: state.agentOption, // Preserve agent options
          tripOption: state.tripOption, // Preserve trip options
          selectedAgent: state.selectedAgent, // Preserve selected agent
          reInitialValue: {
            mealName: state.reInitialValue.mealName,
            resName: state.reInitialValue.resName,
            price: state.reInitialValue.price,
            id: state.reInitialValue.id,
            mealOffereds: state.reInitialValue.mealOffereds,
            locations: state.reInitialValue.locations,
            format: state.reInitialValue.format,
          },
        };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

// Options constants
const TIMING_OPTIONS = [
  { value: '1', label: 'Breakfast' },
  { value: '2', label: 'Snacks' },
  { value: '3', label: 'Lunch' },
  { value: '4', label: 'Dinner' },
];

const FORMAT_OPTIONS = [
  { value: '1', label: 'Fit' },
  { value: '2', label: 'Git' },
  { value: '3', label: 'Delivery' },
];

// Custom hook for API calls - moved outside component to prevent recreating on each render
const createApiService = (apiGlobal) => {
  const fetchAgentOptions = async (signal) => {
    try {
      const response = await fetch(`${apiGlobal}agent_data_load.php`, { signal });
      const data = await response.json();
      
      if (data.status && data.message === 'Success') {
        return data.data.map(agent => ({
          value: agent.id,
          label: agent.name,
        }));
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching agent options:', error);
      }
      return [];
    }
  };

  const fetchAgentById = async (agentId, signal) => {
    try {
      const response = await fetch(`${apiGlobal}/agent_data_fetch.php?id=${agentId}`, { signal });
      const data = await response.json();
      
      if (data.status && data.message === 'Success' && data.data?.length > 0) {
        return {
          value: data.data[0].id,
          label: data.data[0].name,
        };
      }
      return null;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching agent by ID:', error);
      }
      return null;
    }
  };

  const fetchTripOptions = async (agentId, signal) => {
    if (!agentId) return [];
    
    try {
      const response = await fetch(`${apiGlobal}/trip_data.php?agent_id=${agentId}`, { signal });
      const data = await response.json();
      
      if (data.status && data.message === 'Success') {
        return data.data.map(trip => ({
          value: trip.id,
          label: trip.name,
        }));
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching trip options:', error);
      }
      return [];
    }
  };

  const fetchTripDetails = async (tripId, signal) => {
    if (!tripId) return null;
    
    try {
      const response = await fetch(`${apiGlobal}/trip_data_fetch.php?id=${tripId}`, { signal });
      const data = await response.json();
      
      if (data.status && data.message === 'Success' && data.data?.length > 0) {
        const tripData = data.data[0];
        return {
          tripData,
          paxDetails: data.pax_details || [],
          dates: {
            startDate: new Date(tripData.start_date),
            endDate: new Date(tripData.end_date),
          },
        };
      }
      return null;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching trip details:', error);
      }
      return null;
    }
  };

  const fetchPaxData = async (tripId, mealDate, mealOffered, signal) => {
    if (!tripId || !mealDate || !mealOffered) return [];
    
    try {
      const formattedDate = new Date(mealDate).toISOString().split('T')[0];
      const response = await fetch(
        `${apiGlobal}/pax_data_check.php?trip_id=${tripId}&date=${formattedDate}&meal_offered=${mealOffered}`,
        { signal },
      );
      const data = await response.json();
      
      if (data.status && data.message === 'Success') {
        return data.pax_details || [];
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching pax data:', error);
      }
      return [];
    }
  };

  const fetchTripBookingData = async (tripId, signal) => {
    if (!tripId) return [];
    
    const formdata = new FormData();
    formdata.append('trip_id', tripId);
    
    try {
      const response = await fetch(`${apiGlobal}/trip_booking_data.php`, {
        method: 'POST',
        body: formdata,
        signal,
      });
      const data = await response.json();
      
      if (data?.status && data?.message === 'Success') {
        return data.data;
      }
      return [];
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching trip booking data:', error);
      }
      return [];
    }
  };

  const submitBooking = async (formData, signal) => {
    try {
      const response = await fetch(`${apiGlobal}/booking_add.php`, {
        method: 'POST',
        body: formData,
        signal,
      });
      return await response.json();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error submitting booking:', error);
      }
      return { status: false, message: 'Network error occurred' };
    }
  };

  return {
    fetchAgentOptions,
    fetchAgentById,
    fetchTripOptions,
    fetchTripDetails,
    fetchPaxData,
    fetchTripBookingData,
    submitBooking,
  };
};

const BookingForm = ({
  mealName,
  resName,
  price,
  id,
  mealOffereds,
  locations,
  format,
}) => {
  // Contexts
  const {
    setShowAddTripModal,
    tripDataFlag,
    setMealBookingData,
  } = useContext(BookingContext);
  const { userid, apiGlobal } = useContext(AuthContext);
  const [tripBookingData, setTripBookingData] = useState([]);
  
  // State
  const [state, dispatch] = useReducer(bookingReducer, initialState);
  const [selectedPaxs, setSelectedPaxs] = useState([]);
  
  // Create memoized API service
  const apiService = useMemo(() => createApiService(apiGlobal), [apiGlobal]);

  // Memoized derived data
  const restaurantLocationsOptions = useMemo(
    () => state.reInitialValue?.locations?.map(location => ({
      value: location.id,
      label: location.restaurant_name,
    })) || [], 
    [state.reInitialValue?.locations],
  );

  useEffect(() => {
    // Update total meals based on selected passengers
    dispatch({ type: ACTIONS.SET_TOTAL_MEALS, payload: selectedPaxs.length });
    // Update total price
    dispatch({ type: ACTIONS.SET_MEAL_TOTAL_RATE, payload: selectedPaxs.length * parseInt(price, 10) });
  }, [selectedPaxs, price]);

  const filteredTimingOption = useMemo(() => {
    const mealOfferedArray = (state.reInitialValue?.mealOffereds || '').split(',').map(item => item.trim());
    return TIMING_OPTIONS.filter(item => mealOfferedArray.includes(item.value));
  }, [state.reInitialValue?.mealOffereds]);

  const filteredTypeOption = useMemo(() => {
    const formatString = typeof state.reInitialValue?.format === 'string' ? state.reInitialValue.format : '';
    const mealFormatArray = formatString.split(',').map(item => item.trim());
    return FORMAT_OPTIONS.filter(item => mealFormatArray.includes(String(item.value)));
  }, [state.reInitialValue?.format]);

  const isSubmitDisabled = useMemo(
    () => !state.selectedTrip || !state.mealDate || !state.selecttimingOption,
    [state.selectedTrip, state.mealDate, state.selecttimingOption],
  );

  const resetForm = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_FORM });
    setSelectedPaxs([]);
  }, []);

  const calculateDayName = useCallback((mealDate) => {
    if (!mealDate || !state.date.startDate) return;
    
    const mealDateObj = new Date(mealDate);
    const startDateObj = new Date(state.date.startDate);
    
    // Reset hours to ensure we're comparing just the dates
    mealDateObj.setHours(0, 0, 0, 0);
    startDateObj.setHours(0, 0, 0, 0);
    
    const timeDifference = mealDateObj - startDateObj;
    const dayDifference = Math.floor(timeDifference / (1000 * 3600 * 24));
    const dayNumber = dayDifference >= 0 ? dayDifference + 1 : 1;

    dispatch({
      type: ACTIONS.SET_RE_INITIAL_VALUE,
      payload: { dayname: `Day ${dayNumber}` },
    });
  }, [state.date.startDate]);

  const calculateMealTotalPrice = useCallback((totalMeals, mealPrice) => {
    const totalPrice = totalMeals * mealPrice;
    dispatch({ type: ACTIONS.SET_MEAL_TOTAL_RATE, payload: totalPrice });
  }, []);

  // Event handlers
  const handleMealDateChange = useCallback((selectedDate) => {
    dispatch({ type: ACTIONS.SET_MEAL_DATE, payload: selectedDate });
    calculateDayName(selectedDate);
    
    // Reset timing selection properly
    dispatch({ type: ACTIONS.RESET_TIMING });
    
    // Clear pax data and selected paxs
    dispatch({ type: ACTIONS.SET_PAX_DATA, payload: [] });
    setSelectedPaxs([]);
  }, [calculateDayName]);

  const handleTotalMealsChange = useCallback((e) => {
    const newTotalMeal = parseInt(e.target.value, 10) || 0;
    dispatch({ type: ACTIONS.SET_TOTAL_MEALS, payload: newTotalMeal });
    calculateMealTotalPrice(newTotalMeal, parseInt(price, 10));
  }, [calculateMealTotalPrice, price]);

  const handleFieldChange = useCallback((fieldName, selectedOption) => {
    if (!selectedOption) return;
    
    dispatch({
      type: ACTIONS.SET_RE_INITIAL_VALUE,
      payload: {
        [fieldName]: selectedOption,
      },
    });
    
    // If timing field changes, fetch pax data
    if (fieldName === 'timing' && state.mealDate && state.selectedTrip) {
      dispatch({ type: ACTIONS.SET_TIMING_OPTION, payload: selectedOption });
      
      // Use AbortController for fetching pax data
      const abortController = new AbortController();
      const isMounted = true;
      
      apiService.fetchPaxData(
        state.selectedTrip.value, 
        state.mealDate, 
        selectedOption.value,
        abortController.signal,
      ).then((paxData) => {
        if (isMounted) {
          dispatch({ type: ACTIONS.SET_PAX_DATA, payload: paxData });
          setSelectedPaxs([]);
        }
      });
      
      // Clean up function not needed here as it's not in a useEffect
    }
  }, [apiService, state.mealDate, state.selectedTrip]);

  // Also modify the handleAgentChange function to correctly handle agent changes:
const handleAgentChange = useCallback((event, option) => {
  // Clear trip-related data but keep agent-related data
  dispatch({ type: ACTIONS.SET_SELECTED_TRIP, payload: null });
  dispatch({ type: ACTIONS.SET_DATE, payload: { startDate: new Date(), endDate: new Date() } });
  dispatch({
 type: ACTIONS.SET_RE_INITIAL_VALUE,
payload: {
    paxDetails: [],
    tripId: '',
    refno: '',
    triptype: '',
    startDates: null,
    endDate: null,
    dayname: '',
    // Keep the meal-related info
    mealName: state.reInitialValue.mealName,
    resName: state.reInitialValue.resName,
    price: state.reInitialValue.price,
    id: state.reInitialValue.id,
    mealOffereds: state.reInitialValue.mealOffereds,
    locations: state.reInitialValue.locations,
    format: state.reInitialValue.format,
  }, 
});
  dispatch({ type: ACTIONS.SET_MEAL_DATE, payload: null });
  dispatch({ type: ACTIONS.RESET_TIMING });
  dispatch({ type: ACTIONS.SET_PAX_DATA, payload: [] });
  dispatch({ type: ACTIONS.SET_TOTAL_MEALS, payload: 0 });
  dispatch({ type: ACTIONS.SET_MEAL_TOTAL_RATE, payload: 0 });
  
  setSelectedPaxs([]);
  setTripBookingData([]);

  // If no agent selected
  if (!option) {
    dispatch({ 
      type: ACTIONS.SET_SELECTED_AGENT, 
      payload: null, 
    });
    dispatch({ 
      type: ACTIONS.SET_TRIP_OPTION, 
      payload: [], 
    });
    return;
  }

  // Set the newly selected agent
  dispatch({ 
    type: ACTIONS.SET_SELECTED_AGENT, 
    payload: option, 
  });

  // Fetch new trip options
  const abortController = new AbortController();
  let isMounted = true;

  apiService.fetchTripOptions(option.value, abortController.signal)
    .then((tripOptions) => {
      if (isMounted) {
        dispatch({ 
          type: ACTIONS.SET_TRIP_OPTION, 
          payload: tripOptions, 
        });
      }
    })
    .catch((error) => {
      if (error.name !== 'AbortError') {
        console.error('Error fetching trip options:', error);
      }
    });
  // eslint-disable-next-line
  return () => {
    isMounted = false;
    abortController.abort();
  };
}, [apiService, state.reInitialValue]);

  const fetchTripBookingData = useCallback((tripId) => {
    if (!tripId) return;
    
    const abortController = new AbortController();
    let isMounted = true;
    
    apiService.fetchTripBookingData(tripId, abortController.signal)
      .then((data) => {
        if (isMounted) {
          setTripBookingData(data || []);
        }
      });
    // eslint-disable-next-line
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [apiService]);

  const handleTripChange = useCallback((event, option) => {
    if (!option) {
      // Don't reset the form completely, just clear trip-specific data
      dispatch({ type: ACTIONS.SET_SELECTED_TRIP, payload: null });
      dispatch({ type: ACTIONS.SET_DATE, payload: { startDate: new Date(), endDate: new Date() } });
      dispatch({
 type: ACTIONS.SET_RE_INITIAL_VALUE,
payload: {
        paxDetails: [],
        tripId: '',
        refno: '',
        triptype: '',
        startDates: null,
        endDate: null,
        dayname: '',
      }, 
});
      dispatch({ type: ACTIONS.SET_PAX_DATA, payload: [] });
      dispatch({ type: ACTIONS.SET_MEAL_DATE, payload: null });
      dispatch({ type: ACTIONS.RESET_TIMING });
      dispatch({ type: ACTIONS.SET_TOTAL_MEALS, payload: 0 });
      dispatch({ type: ACTIONS.SET_MEAL_TOTAL_RATE, payload: 0 });
      setSelectedPaxs([]);
      setTripBookingData([]);
      return;
    }
  
    // Instead of calling resetForm() which resets everything including trip options,
    // just reset the trip-specific data
    dispatch({ type: ACTIONS.SET_SELECTED_TRIP, payload: option });
    dispatch({ type: ACTIONS.SET_MEAL_DATE, payload: null });
    dispatch({ type: ACTIONS.RESET_TIMING });
    dispatch({ type: ACTIONS.SET_PAX_DATA, payload: [] });
    dispatch({ type: ACTIONS.SET_TOTAL_MEALS, payload: 0 });
    dispatch({ type: ACTIONS.SET_MEAL_TOTAL_RATE, payload: 0 });
    setSelectedPaxs([]);
    
    // Fetch trip booking data
    fetchTripBookingData(option.value);
    
    // Fetch trip details with AbortController
    const abortController = new AbortController();
    let isMounted = true;
    
    apiService.fetchTripDetails(option.value, abortController.signal)
      .then((tripDetails) => {
        if (!isMounted) return;
        
        if (tripDetails) {
          const { tripData, paxDetails, dates } = tripDetails;
          
          dispatch({ type: ACTIONS.SET_DATE, payload: dates });
          
          dispatch({
            type: ACTIONS.SET_RE_INITIAL_VALUE,
            payload: {
              paxDetails,
              tripId: tripData.trip_uin,
              refno: tripData.ref_no,
              triptype: tripData.trip_type,
              startDates: dates.startDate,
              endDate: dates.endDate,
            },
          });
        }
      });
    // eslint-disable-next-line
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [apiService, fetchTripBookingData]);

  const showTripModal = useCallback(() => {
    setShowAddTripModal(true);
    setMealBookingData(prevState => ({
      ...prevState,
      showMealBookingModal: false,
    }));
  }, [setShowAddTripModal, setMealBookingData]);

  // Form submission
  const handleBookingSubmit = useCallback((e) => {
    e.preventDefault();
    
    // Form validation
    if (!state.selectedTrip?.value) {
      toast.error('Trip selection is required');
      return;
    }
    
    if (!state.selectedAgent?.value) {
      toast.error('Agent selection is required');
      return;
    }
    
    if (!state.mealDate) {
      toast.error('Please select a meal date');
      return;
    }
    
    if (!state.reInitialValue.resLocation?.value) {
      toast.error('Reservation location is required');
      return;
    }
    
    if (!state.selecttimingOption?.value) {
      toast.error('Timing is required');
      return;
    }
    
    if (!state.reInitialValue.format?.value) {
      toast.error('Select Format');
      return;
    }
    
    if (!state.reInitialValue.dayname) {
      toast.error('Day name is required');
      return;
    }
    
    if (!price) {
      toast.error('Price is required');
      return;
    }
    
    if (!state.totalMeals) {
      toast.error('Total meals are required');
      return;
    }
    
    if (!id) {
      toast.error('Meal ID not found');
      return;
    }
    
    if (!userid) {
      toast.error('User ID not found, please refresh again');
      return;
    }

    if (selectedPaxs.length === 0) {
      toast.error('No Pax Selected');
      return;
    }
    
    if (selectedPaxs.length > state.totalMeals) {
      toast.error('Number of Paxs should be less than or equal to total meals');
      return;
    }

    // Format date for API
    const bookingDate = new Date(state.mealDate);
    const formatDate = date => `${date
      .getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const formattedBookingDate = formatDate(bookingDate);
    
    // Calculate total amount
    const totalAmount = parseInt(price, 10) * state.totalMeals;

    // Prepare form data
    const formData = new FormData();
    formData.append('user_id', userid);
    formData.append('agent_id', state.selectedAgent.value);
    formData.append('booking_date', formattedBookingDate);
    formData.append('trip_id', state.selectedTrip.value);
    formData.append('location_id', state.reInitialValue.resLocation.value);
    formData.append('meal_id', id);
    formData.append('meal_date', formattedBookingDate);
    formData.append('meal_offered', state.selecttimingOption.value);
    formData.append('day_name', state.reInitialValue.dayname);
    formData.append('meal_amount', price);
    formData.append('total_meal', state.totalMeals);
    formData.append('total_amount', totalAmount);
    formData.append('pax_ids', selectedPaxs);

    // Submit booking with AbortController
    const abortController = new AbortController();
    
    apiService.submitBooking(formData, abortController.signal)
      .then((response) => {
        if (response.status && response.message === 'Success') {
          toast.success('Meal Booking Successfully...');
          setMealBookingData(prevState => ({
            ...prevState,
            locations: '',
            mealOffereds: '',
            finalprice: '', 
            format: '', 
            id: '', 
            mealname: '', 
            restaurantname: '',
            showMealBookingModal: false,
          }));
        } else if (response.status && response.balance_flag === 1) {
          toast.error(response.message);
        } else {
          toast.error('Error in Meal Booking...');
        }
      });
  }, [
    apiService, id, price, selectedPaxs, state.mealDate, state.reInitialValue, 
    state.selecttimingOption, state.selectedAgent, state.selectedTrip, 
    state.totalMeals, userid, setMealBookingData,
  ]);

  // Effects
   
  // Initialize agent options on component mount
  useEffect(() => {
    if (tripDataFlag.addMealINBooking === true) {
      return; // Skip if not needed
    }
    
    // Create abort controller for cleanup
    const abortController = new AbortController();
    let isMounted = true;
    
    // Fetch agent options
    apiService.fetchAgentOptions(abortController.signal)
      .then((options) => {
        if (isMounted) {
          dispatch({ type: ACTIONS.SET_AGENT_OPTION, payload: options });
        }
      });
    
    // Cleanup function
    // eslint-disable-next-line
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [apiService, tripDataFlag.addMealINBooking]);

  // Initialize meal details
  useEffect(() => {
    if (mealName && resName) {
      dispatch({
        type: ACTIONS.SET_RE_INITIAL_VALUE,
        payload: {
          mealName,
          resName,
          price,
          id,
          mealOffereds,
          locations,
          format,
        },
      });
    }
  }, [mealName, resName, price, id, mealOffereds, locations, format]);

  // Handle trip selection from external source
  useEffect(() => {
    if (!tripDataFlag?.addMealINBooking) return;
    
    const abortController = new AbortController();
    let isMounted = true;
    
    const loadExternalTripData = async () => {
      const tripOption = {
        value: tripDataFlag.tripID,
        label: tripDataFlag.name,
      };
      
      // Fetch and set agent info
      try {
        const agent = await apiService.fetchAgentById(tripDataFlag.agentid, abortController.signal);
        if (!isMounted) return;
        
        if (agent) {
          dispatch({ type: ACTIONS.SET_SELECTED_AGENT, payload: agent });
          dispatch({ type: ACTIONS.SET_AGENT_OPTION, payload: [agent] });
        }
        
        // Set selected trip and fetch its details
        dispatch({ type: ACTIONS.SET_SELECTED_TRIP, payload: tripOption });
        
        // Fetch trip booking data
        fetchTripBookingData(tripOption.value);
        
        const tripDetails = await apiService.fetchTripDetails(tripOption.value, abortController.signal);
        if (!isMounted) return;
        
        if (tripDetails) {
          const { tripData, paxDetails, dates } = tripDetails;
          
          dispatch({ type: ACTIONS.SET_DATE, payload: dates });
          
          dispatch({
            type: ACTIONS.SET_RE_INITIAL_VALUE,
            payload: {
              paxDetails,
              tripId: tripData.trip_uin,
              refno: tripData.ref_no,
              triptype: tripData.trip_type,
              startDates: dates.startDate,
              endDate: dates.endDate,
            },
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          console.error('Error loading external trip data:', error);
        }
      }
    };
    
    loadExternalTripData();
    
    // eslint-disable-next-line
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [tripDataFlag, apiService, fetchTripBookingData]);


  return (
    <div className="d-flex">
      <form onSubmit={handleBookingSubmit}>
        <Grid container spacing={2}>
          {/* Agent and Trip Selection Row */}
          <Grid item xs={4} className="m-2">
            <Autocomplete
              options={state.agentOption}
              getOptionLabel={option => option?.label || ''}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Agent Option"
                  variant="outlined"
                  placeholder="Select Agent"
                />
              )}
              size="small"
              onChange={handleAgentChange}
              value={state.selectedAgent}
              disabled={tripDataFlag?.addMealINBooking}
              isSearchable
              isClearable
            />
          </Grid>
        
          <Grid item xs={4} className="m-2">
            <Autocomplete
              options={state.tripOption}
              getOptionLabel={option => option.label || ''}
              renderInput={params => (
                <TextField 
                  {...params} 
                  label="Trip Option" 
                  variant="outlined"
                />
              )}
              size="small"
              onChange={handleTripChange}
              value={state.selectedTrip}
              readOnly={tripDataFlag?.addMealINBooking}
            />
          </Grid>
        
          {tripDataFlag?.addMealINBooking === false && (
            <Grid item xs={2} className="m-2">
              <Button 
                variant="primary-outline" 
                type="button" 
                fullWidth 
                onClick={showTripModal}
              >
                Add trip
              </Button>
            </Grid>
          )}

          {/* Trip Details Row */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={3}>
              <TextField
                label="Trip ID"
                variant="outlined"
                fullWidth
                name="tripID"
                size="small"
                value={state?.reInitialValue?.tripId || ''}
                InputLabelProps={{
                  shrink: !!state.reInitialValue.tripId,
                }}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Ref No"
                variant="outlined"
                fullWidth
                name="refNo"
                size="small"
                value={state?.reInitialValue?.refno || ''}
                InputLabelProps={{
                  shrink: !!state.reInitialValue.refno,
                }}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Start Date"
                variant="outlined"
                fullWidth
                value={state?.reInitialValue?.startDates
                  ? state.reInitialValue.startDates.toISOString().split('T')[0] 
                  : ''}
                name="sDate"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="End Date"
                variant="outlined"
                fullWidth
                value={state?.reInitialValue?.endDate
                  ? state.reInitialValue.endDate.toISOString().split('T')[0] 
                  : ''}
                name="eDate"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        
          {/* Meal Details Row */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={4}>
              <TextField
                label="Meal Name"
                variant="outlined"
                fullWidth
                name="mealName"
                size="small"
                value={state?.reInitialValue?.mealName || ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Restaurant Name"
                variant="outlined"
                fullWidth
                name="resName"
                size="small"
                value={state?.reInitialValue?.resName || ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={4}>
              <Autocomplete
                key={`location-${state.selectedTrip?.value || 'no-trip'}`}
                disablePortal
                size="small"
                options={restaurantLocationsOptions}
                renderInput={params => (
                  <TextField 
                    {...params} 
                    label="Select Res Location"
                  />
                )}
                onChange={(e, option) => handleFieldChange('resLocation', option)}
                disabled={!state.selectedTrip}
              />
            </Grid>
          </Grid>

          {/* Meal Date and Count Row */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={4}>
              <TextField
                label="Meal Date"
                variant="outlined"
                fullWidth
                name="mealDate"
                size="small"
                type="date"
                value={state.mealDate || ''}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: state?.reInitialValue?.startDates
                    ? state.reInitialValue.startDates.toISOString().split('T')[0] 
                    : '',
                  max: state?.reInitialValue?.endDate
                    ? state.reInitialValue.endDate.toISOString().split('T')[0] 
                    : '',
                }}
                onChange={e => handleMealDateChange(e.target.value)}
                disabled={!state.selectedTrip}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Day Name"
                variant="outlined"
                fullWidth
                name="dayName"
                size="small"
                value={state?.reInitialValue?.dayname || ''}
                InputLabelProps={{
                  shrink: !!state?.reInitialValue?.dayname,
                }}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Total Meal"
                variant="outlined"
                fullWidth
                name="totalmeal"
                size="small"
                value={state.totalMeals || 0}
                InputProps={{ readOnly: true }}
                // onChange={handleTotalMealsChange}
                type="number"
                disabled={!state.selectedTrip}
              />
            </Grid>
          </Grid>
        
          {/* Timing, Format and Total Row */}
          <Grid container spacing={2} className="m-2">
            <Grid item xs={4}>
              <Autocomplete
                key={`timing-${state.mealDate || 'no-date'}`}
                disablePortal
                size="small"
                options={filteredTimingOption}
                renderInput={params => (
                  <TextField 
                    {...params} 
                    label="Select Timing"
                  />
                )}
                onChange={(e, option) => handleFieldChange('timing', option)}
                disabled={!state.mealDate}
                value={state.selecttimingOption}
              />
            </Grid>
 
            <Grid item xs={4}>
              <Autocomplete
                disablePortal
                size="small"
                disableClearable
                options={filteredTypeOption}
                renderInput={params => (
                  <TextField 
                    {...params} 
                    label="Select Format"
                  />
               )}
                onChange={(e, option) => handleFieldChange('format', option)}
                // ref={autocompleteRefs.format}
                disabled={!state.mealDate}
              />
            </Grid>
 
            <Grid item xs={4}>
              <TextField
                label="Total Amount"
                variant="outlined"
                fullWidth
                name="totalamount"
                size="small"
                value={state?.mealTotalRate || ''}
                InputLabelProps={{
                 shrink: true,
               }}
                InputProps={{
                 readOnly: true,
               }}
              />
            </Grid>
          </Grid>
 
          <Grid item xs={4} className="m-2">
            <FormControlLabel
              value="1"
              control={<Radio checked={state?.reInitialValue?.triptype === '1'} readOnly />}
              label="Group"
              name="triptype"
            />
            <FormControlLabel
              value="2"
              control={<Radio checked={state?.reInitialValue?.triptype === '2'} readOnly />}
              label="Family"
              name="triptype"
            />
          </Grid>
 
          <Grid container spacing={2} className="m-2">
            <Grid item xs={12}>
              <PaxDetailsTable 
                paxDetails={state.paxData || []}
                selectedPaxs={selectedPaxs}
                setSelectedPaxs={setSelectedPaxs}
                triptype={state.reInitialValue.triptype}
              />
            </Grid>
          </Grid>
 
          <Grid item xs={12}>
            <Button 
              variant="primary" 
              type="submit" 
              fullWidth
              disabled={!state.selectedTrip || !state.mealDate || !state.selecttimingOption}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
      <div style={{
  border: '1px solid #ccc',
  padding: '20px',
  borderRadius: '10px',
  marginTop: '20px',
  width: '80%',
  maxHeight: '100vh', // Use viewport height for scrollable area
  overflowY: 'auto', // Enable vertical scroll
}}
      >
        <h5>Trip Booking Details</h5>
        <hr />
        {tripBookingData?.length > 0 && tripBookingData.map((tripData, index) => (
          <ShowTripInfo key={tripData.id || index} tripBookingInfo={tripData} />
  ))}
      </div>
    </div>
     
   );
 }; 
 
 BookingForm.propTypes = {
   mealName: PropTypes.string.isRequired,
   resName: PropTypes.string.isRequired,
   price: PropTypes.string.isRequired,
   id: PropTypes.string.isRequired,
   mealOffereds: PropTypes.string.isRequired,
   format: PropTypes.string.isRequired,
   locations: PropTypes.arrayOf(
     PropTypes.shape({
       id: PropTypes.string.isRequired,
       restaurant_name: PropTypes.string.isRequired,
     }),
   ).isRequired,
 };
 
 export default BookingForm;
 
 // import React, {
 //   useContext, useState, useEffect, useReducer, useMemo,
 //  } from 'react';
 //  import toast from 'react-hot-toast';
 //  import PropTypes from 'prop-types';
 //  import { Field, Form } from 'react-final-form';
 //  import Select from 'react-select';
 //  import {
 //   FormButtonToolbar, FormContainer, FormGroup, FormGroupField, FormGroupLabel, FormHalfContainer, 
 //  } from '@/shared/components/form/FormElements';
 //  import DatePicker from 'react-datepicker';
 //  import { Button } from '@/shared/components/Button';
 //  import PaxDetailsTable from './PaxDetailsTable'; 
 //  import { BookingContext } from '../../../../../../context/BookingContext';
 //  import { AuthContext } from '../../../../../../context/AuthContext';
 
 //  const initialState = {
 //   totalMeals: 0,
 //   mealTotalRate: 0,
 //   mealDate: null,
 //   selectedTrip: [],
 //   paxData: [],
 //   tripOption: [],
 //   tripData: {},
 //   agentOption: [],
 //   selectedAgent: [],
 //   selecttimingOption: {},
 //   reInitialValue: {},
 //   selectesPax: null,
 //   date: {
 //     startDate: new Date(),
 //     endDate: new Date(),
 //   },
 // };
 
 // function bookingReducer(state, action) {
 //   switch (action.type) {
 //     case 'SET_TOTAL_MEALS':
 //       return { ...state, totalMeals: action.payload };
 //     case 'SET_MEAL_TOTAL_RATE':
 //       return { ...state, mealTotalRate: action.payload };
 //     case 'SET_MEAL_DATE':
 //       return { ...state, mealDate: action.payload };
 //     case 'SET_PAX_DATA':
 //       return { ...state, paxData: action.payload };
 //     case 'SET_SELECTED_TRIP':
 //       return { ...state, selectedTrip: action.payload };
 //     case 'SET_TRIP_OPTION':
 //       return { ...state, tripOption: action.payload };
 //     case 'SET_TIMING_OPTION':
 //       return { ...state, selecttimingOption: action.payload };
 //     case 'SET_SELECTED_PAX':
 //       return { ...state, selectesPax: action.payload };
 //     case 'SET_AGENT_OPTION':
 //       return { ...state, agentOption: action.payload };
 //       case 'SET_SELECTED_AGENT':
 //       return { ...state, selectedAgent: action.payload };
 //     case 'SET_TRIP_DATA':
 //       return { ...state, tripData: action.payload };
 //       case 'SET_RE_INITIAL_VALUE':
 //         return {
 //           ...state,
 //           reInitialValue: {
 //             ...state.reInitialValue,
 //             ...action.payload, // Merge new values into reInitialValue
 //           },
 //         };
 //     case 'SET_DATE':
 //       return { ...state, date: action.payload };
 //     default:
 //       throw new Error(`Unhandled action type: ${action.type}`);
 //   }
 // }
 
 // const BookingForm = ({
 //   mealName,
 //   resName,
 //   price,
 //   id,
 //   mealOffereds,
 //   locations,
 //   format,
 // }) => {
 //   const {
 //     setShowAddTripModal, tripDataFlag, setTripDataFlag, setPaxs, paxs, setMealBookingData,
 //    } = useContext(BookingContext);
 //      const [state, dispatch] = useReducer(bookingReducer, initialState);
 //      const { apiGlobal, userid } = useContext(AuthContext); 
 //      const [selectedPaxs, setSelectedPaxs] = useState([]);
 
 
 //      const timingOption = [
 //       { value: '1', label: 'Breakfast' },
 //       { value: '2', label: 'Snacks' },
 //       { value: '3', label: 'Lunch' },
 //       { value: '4', label: 'Dinner' },
 //     ];
     
 //     const typeOption = [
 //       { value: '1', label: 'Fit' },
 //       { value: '2', label: 'Git' },
 //       { value: '3', label: 'Delivery' },
 //     ];
 
 //     const showTripModal = () => {
 //       setShowAddTripModal(true);
 //       setMealBookingData(prevState => ({
 //         ...prevState, // spread the previous state to retain other values
 //         showMealBookingModal: false, // set showMealBookingModal to true
 //       }));
 //     };
     
     
 
 // const handleBookingSubmit = async (value) => {
 //     console.log(JSON.stringify(value, null, 2));
 
 
 //     if (selectedPaxs.length === 0) {
 //       toast.error('No Pax Selected');
 //       return;
 //     }
 
 
 //     const utcBookingDate = state.mealDate;
 //     const BookingDate = new Date(utcBookingDate);
 //     const istOffset = 5.5 * 60 * 60 * 1000;
 //      const bookingDateIST = new Date(BookingDate.getTime() + istOffset);
 //       // Format the dates (DD-MM-YYYY)
 //       const formatDate = dates => `${dates.getFullYear()}-${String(dates.getMonth() + 1)
 //         .padStart(2, '0')}-${String(dates.getDate()).padStart(2, '0')}`;
   
 //         const formattedBookingDate = formatDate(bookingDateIST);
 
 //         const totalamount = value.price * value.totalMeals;
 
 //       const formData = new FormData();
 //       formData.append('user_id', userid);
 //       formData.append('agent_id', state.selectedAgent.value);
 //       formData.append('booking_date', formattedBookingDate);
 //       formData.append('trip_id', state.selectedTrip.value);
 //       formData.append('location_id', value.resLocation.value);
 //       formData.append('meal_id', id);
 //       formData.append('meal_date', formattedBookingDate);
 //       formData.append('meal_offered', value.timing.value);
 //       formData.append('day_name', value.dayname);
 //       formData.append('meal_amount', value.price);
 //       formData.append('total_meal', value.totalMeals);
 //       formData.append('total_amount', totalamount);
 //       formData.append('pax_ids', selectedPaxs);
 
 //       formData.forEach((values, key) => {
 //         console.log(`${key}: ${values}`);
 //       });
 
 //      try {
 //       const url = await fetch(`${apiGlobal}/booking_add.php`, {
 //          method: 'POST',
 //          body: formData,
 //       });
 //       const response = await url.json();
 //       if (response.status && response.message === 'Success') {
 //         toast.success('Meal Booking Successfully...');
 //         setMealBookingData(prevState => ({
 //           ...prevState,
 //           locations: '',
 //           mealOffereds: '',
 //           finalprice: '', 
 //           format: '', 
 //           id: '', 
 //           mealname: '', 
 //           restaurantname: '',
 //           showMealBookingModal: false,
 //         }));
 //       } else if (response.status && response.balance_flag === 1) {
 //         toast.error(response.message);
 //       } else {
 //         toast.error('Error in Meal Booking...');
 //       }
 //      } catch (error) {
 //        console.log(error);
 //      }
 // };
 
 // const calculateDayName = (mealdate) => {
 //   if (mealdate && state.date.startDate) {
 //     const mealDateOnly = new Date(mealdate.setHours(0, 0, 0, 0));
 //     const startDateOnly = new Date(state.date.startDate.setHours(0, 0, 0, 0));
 //     const timeDifference = mealDateOnly - startDateOnly;
 //     const dayDifference = Math.floor(timeDifference / (1000 * 3600 * 24));
 //     const dayNumber = dayDifference >= 0 ? dayDifference + 1 : 1;
 
 //     dispatch({
 //       type: 'SET_RE_INITIAL_VALUE',
 //       payload: { ...state.reInitialValue, dayname: `Day ${dayNumber}` },
 //     });
 //   }
 // };
 
 // const fetchAgentOption = async () => {
 //     try {
 //       const url = await fetch(`${apiGlobal}/agent_data_load.php`);
 //       const response = await url.json();
 //        console.log(response);
 //       if (response.status && response.message === 'Success') {
 //         const agentOptions = response.data.map(agent => ({
 //           value: agent.id, 
 //           label: agent.name,
 //         }));
 //         dispatch({
 //           type: 'SET_AGENT_OPTION',
 //           payload: agentOptions,
 //         });
 //       }
 //     } catch (error) {
 //       console.log(error);
 //     }
 //    };
 
 //    useEffect(() => {
 //     if (tripDataFlag.addMealINBooking === false) {
 //       fetchAgentOption();
 //     }
 //    }, []);
 
 // const handleBookingCheck = async (option) => {
 //   dispatch({
 //  type: 'SET_TIMING_OPTION',
 //  payload: option,
 //   });
 //   console.log(state.selectedTrip);
 //   const utcSelectedDate = state.mealDate;
 //   // Convert to IST
 //   const selectDate = new Date(utcSelectedDate);
 //   // IST offset is +5:30
 //   const istOffset = 5.5 * 60 * 60 * 1000;
 //   const selectDateIST = new Date(selectDate.getTime() + istOffset);
 //   // Format the IST date to ISO format (YYYY-MM-DD)
 //   const formattedSelectedDate = selectDateIST.toISOString().split('T')[0];
 
 //      try {
 //       const response = await fetch(`${apiGlobal}/pax_data_check.php?
 //         trip_id=${state.selectedTrip.value}&date=${formattedSelectedDate}&meal_offered=${option.value}`);
 //       const data = await response.json();
 //      console.log(data);
 //       if (data.status && data.message === 'Success') {
 //         console.log(data.pax_details);
 //         dispatch({
 //           type: 'SET_PAX_DATA',
 //           payload: data.pax_details,
 //         });
 //       }
 //      } catch (e) {
 //       console.log(e);
 //      }
 // };
 
 
 
 
 // const handleMealDateChange = (selectedDate) => {
 //   dispatch({ type: 'SET_MEAL_DATE', payload: selectedDate });
 //   calculateDayName(selectedDate);
 //   handleBookingCheck(state.selecttimingOption.value);
 // };
 
 
 
 // const handleFieldChange = (fieldName, selectedOption) => {
 //   if (!selectedOption) {
 //     console.error('Selected Option is undefined or null');
 //     return;
 //   }
 //   dispatch({
 //     type: 'SET_RE_INITIAL_VALUE',
 //     payload: {
 //       [fieldName]: selectedOption, // Update the specific field in reInitialValue
 //     },
 //   });
 // };
 
 
 // // fetch trip option
 // const fetchTripOption = async (e) => {
 //   if (e) {
 //     dispatch({
 //       type: 'SET_SELECTED_AGENT',
 //       payload: e,
 //     });
 //     dispatch({
 //       type: 'SET_TRIP_OPTION',
 //       payload: [],
 //     });
 //     dispatch({
 //       type: 'SET_SELECTED_TRIP',
 //       payload: [],
 //     });
 //   }
 //   try {
 //     const url = await fetch(`${apiGlobal}/trip_data.php?agent_id=${e.value}`);
 //     const response = await url.json();
 //     if (response.status && response.message === 'Success') {
 //       const tripOptions = response.data.map(item => ({
 //         value: item.id,
 //         label: item.name,
 //       }));
 //       dispatch({ type: 'SET_TRIP_OPTION', payload: tripOptions });
 //     }
 //   } catch (error) {
 //     console.log(error);
 //   }
 // };
 
 //  useEffect(() => {
 //     if (tripDataFlag.addMealINBooking === false) {
 //       fetchTripOption();
 //     }
 //   }, []);
 
 
 // // fetch trip data
 // const fetchTripData = async (e, form) => {
 //   dispatch({
 //     type: 'SET_RE_INITIAL_VALUE',
 //     payload: { ...state.reInitialValue, dayname: '' },
 //   });
 //   dispatch({ type: 'SET_MEAL_DATE', payload: null });
 //   setPaxs([]);
 //   dispatch({ type: 'SET_SELECTED_TRIP', payload: e });
 
 //   try {
 //     let url;
 //     if (tripDataFlag.addMealINBooking) {
 //       url = await fetch(`${apiGlobal}/trip_data_fetch.php?id=${tripDataFlag.tripID}`);
 //     } else {
 //       url = await fetch(`${apiGlobal}/trip_data_fetch.php?id=${e.value}`);
 //     }
 
 //     const response = await url.json();
 //     // console.log(response);
 //     if (response.status && response.message === 'Success') {
 //       const tripDatas = response.data[0];
 //       const parsedStartDate = new Date(tripDatas.start_date);
 //       const parsedEndDate = new Date(tripDatas.end_date);
  
 //       dispatch({
 //         type: 'SET_DATE',
 //         payload: { startDate: parsedStartDate, endDate: parsedEndDate },
 //       });
 
 //       dispatch({
 //         type: 'SET_RE_INITIAL_VALUE',
 //         payload: {
 //           ...state.reInitialValue,
 //           paxDetails: response.pax_details || [],
 //           tripId: tripDatas.trip_uin,
 //           refno: tripDatas.trip_ref_no,
 //           triptype: tripDatas.trip_type,
 //           startDates: parsedStartDate,
 //           endDate: parsedEndDate,
 //           selectedTrip: tripDataFlag.addMealINBooking ? tripDataFlag.tripID : e,
 //         },
 //       });
 
 //       form.change('tripId', tripDatas.trip_uin);
 //       form.change('triptype', tripDatas.trip_type);
 //     } else {
 //       dispatch({ type: 'SET_TRIP_DATA', payload: {} });
 //     }
 //   } catch (error) {
 //     console.log(error);
 //   }
 // };
 
 
 // useEffect(() => {
 //   if (mealName && resName) {
 //     dispatch({
 //       type: 'SET_RE_INITIAL_VALUE',
 //       payload: {
 //         ...state.reInitialValue,
 //         mealName,
 //         resName,
 //         price,
 //         id,
 //         mealOffereds,
 //         locations,
 //         format,
 //       },
 //     });
 //   }
 // }, [mealName, resName, price, id, mealOffereds, locations, format]);
 
 
 //  const restaurantLocationsOptions = state.reInitialValue?.locations?.map(location => ({
 //   value: location.id,
 //   label: location.restaurant_name,
 // }));
 
 // // This will correctly split and trim the mealOffereds string
 // const mealOfferedArray = (state.reInitialValue?.mealOffereds || '').split(',').map(item => item.trim());
 
 // // Ensure the mealOfferedArray and timingOption values are properly compared
 // const filteredtimingOption = timingOption
 // .filter(item => mealOfferedArray.includes(item.value)); 
 // // compare item.value directly, no need for String(item.value)
 
 // console.log(filteredtimingOption);
 
 
 
 
 
 // // useEffect(() => {
 // //   // Update timing options when mealOffereds changes
 // //   dispatch({
 // //     type: 'SET_TIMING_OPTION',
 // //     payload: filteredtimingOption,
 // //   });
 
 // //   console.log('Updated timing options:', filteredtimingOption); // Log the updated timing options
 // // }, [mealOffereds]); // Effect depends on the mealOffereds prop or state
 
 
 
 // const filteredtypeOption = useMemo(() => {
 //   // Ensure state.reInitialValue.format is a string
 //   const formatString = typeof state.reInitialValue?.format === 'string' ? state.reInitialValue.format : '';
 
 //   // Split the format string into an array and trim each item
 //   const mealFormatArray = formatString.split(',').map(item => item.trim());
 
 //   // Ensure typeOption is an array
 //   const options = Array.isArray(typeOption) ? typeOption : [];
 
 //   // Filter typeOption based on mealFormatArray
 //   return options.filter((item) => {
 //     // Ensure item.value is defined and convert it to a string
 //     const itemValue = item?.value !== undefined ? String(item.value) : '';
 //     return mealFormatArray.includes(itemValue);
 //   });
 // }, [state.reInitialValue?.format, typeOption]);
 
 // // Calculate the total meal price
 // const calculateMealTotalPrice = (tMeals, mealPrice) => {
 //   const totalPrice = tMeals * mealPrice;
 //   dispatch({ type: 'SET_MEAL_TOTAL_RATE', payload: totalPrice });
 //   dispatch({
 //     type: 'SET_RE_INITIAL_VALUE',
 //     payload: { ...state.reInitialValue, mealTotalRate: totalPrice },
 //   });
 // };
 
 // const handleTotalMealsChange = (e) => {
 //   const newTotalMeal = parseInt(e.target.value, 10) || 0;
 //   dispatch({ type: 'SET_TOTAL_MEALS', payload: newTotalMeal });
 //   calculateMealTotalPrice(newTotalMeal, parseInt(price, 10));
 //   dispatch({
 //     type: 'SET_RE_INITIAL_VALUE',
 //     payload: { ...state.reInitialValue, totalMeals: newTotalMeal },
 //   });
 // };
 
 // // console.log('state:', state);
 
 
 
 // const fetchAgentName = async (agentID) => {
 //   try {
 //     const response = await fetch(`${apiGlobal}agent_data_fetch.php?id=${agentID}`);
 //     const data = await response.json();
 
 //     // Ensure the response is successful
 //     if (data.status && data.message === 'Success' && data.data && data.data.length > 0) {
 //        const agent = {
 //         value: data.data[0].id,
 //         label: data.data[0].name,
         
 //        };
 //       dispatch({
 //         type: 'SET_SELECTED_AGENT',
 //         payload: agent,
 //       });
 //       dispatch({
 //         type: 'SET_AGENT_OPTION',
 //         payload: agent,
 //       });
 //     } else {
 //       console.error('Something went wrong while fetching agent name: ', data.message);
 //     }
 //   } catch (error) {
 //     console.error('Error fetching agent data:', error);
 //   }
 // };
 
 
 // useEffect(() => {
 //     if (tripDataFlag.addMealINBooking) {
 //       fetchTripData({ value: tripDataFlag.tripID });
 //       fetchAgentName(tripDataFlag.agentid);
 //       dispatch({
 //         type: 'SET_TRIP_OPTION',
 // payload: {
 //           value: tripDataFlag.tripID, label: tripDataFlag.name,
 //         },
 //       });
       
 //       dispatch({
 //         type: 'SET_SELECTED_TRIP',
 // payload: 
 //           {
 //             value: tripDataFlag.tripID, label: tripDataFlag.name,
 //           },
         
 //       });
 //     }
 //   }, [tripDataFlag]);
 
 //   const handleChange = (e) => {
 //     console.log(e.target.value);
 //   };
 
 // return (
 //   <Form onSubmit={handleBookingSubmit} initialValues={state.reInitialValue || { totalMeals: 0 }}>
 
 //     {({ handleSubmit, form }) => (
 //       <FormContainer onSubmit={handleSubmit}>
 //         <div className="row">
 //           {/* Start Date Field */}
 //           <div className="row">
 //             <div
 //               className="col-md-4"
 //             >
 //               <FormGroup>
 //                 <FormGroupLabel>
 //                   Select Agent<span style={{ color: 'red' }}>*</span>
 //                 </FormGroupLabel>
 //                 <FormGroupField>
 //                   <Field
 //                     name="selectedTrip"
 //                     render={({ input }) => (
 //                       <Select
 //                         {...input}
 //                         className="react-select w-100"
 //                         options={state.agentOption}
 //                         value={state.selectedAgent}
 //                         onChange={e => fetchTripOption(e)}
 //                         isClearable={false}
 //                         placeholder="Select a Agent"
 //                         required
 //                       />
 //           )}
 //                   />
 //                 </FormGroupField>
              
 //               </FormGroup>
 //             </div>
 //             <div
 //               className="col-md-4"
 //             >
 //               <FormGroup>
 //                 <FormGroupLabel>
 //                   Select Trip<span style={{ color: 'red' }}>*</span>
 //                 </FormGroupLabel>
 //                 <FormGroupField>
 //                   <Field
 //                     name="selectedTrip"
 //                     render={({ input }) => (
 //                       <Select
 //                         {...input}
 //                         className="react-select w-100"
 //                         options={state.tripOption}
 //                         value={state.selectedTrip}
 //                         onChange={e => fetchTripData(e, form)}
 //                         isClearable={false}
 //                         placeholder="Select a trip"
 //                         required
 //                       />
 //           )}
 //                   />
 //                 </FormGroupField>
              
 //               </FormGroup>
 //             </div>
             
 //             {!tripDataFlag.addMealINBooking && (
 //             <div className="col-md-4 mt-4">
 //               <Button variant="primary-outline" type="button" onClick={showTripModal}>
 //                 Add Trip
 //               </Button>
 //             </div>       
 //                       )}
            
             
             
 //           </div>
           
 //           <div className="row">
 //             <div className="col-md-4">
 //               <FormGroup>
 //                 <FormGroupLabel>
 //                   Trip ID<span style={{ color: 'red' }}>*</span>
 //                 </FormGroupLabel>
 //                 <FormGroupField>
 //                   <Field
 //                     name="tripId"
 //                     component="input"
 //                     type="text"
 //                     className="form-control"
 //                     placeholder="Trip ID"
 //                     readOnly
 //                   />
 //                 </FormGroupField>
 //               </FormGroup>
              
 //             </div>
 //             <div className="col-md-4">
 //               <FormGroup>
 //                 <FormGroupLabel>
 //                   Ref no<span style={{ color: 'red' }}>*</span>
 //                 </FormGroupLabel>
 //                 <FormGroupField>
 //                   <Field
 //                     name="refno"
 //                     component="input"
 //                     type="text"
 //                     className="form-control"
 //                     placeholder="Ref no"
 //                     readOnly
 //                   />
 //                 </FormGroupField>
 //               </FormGroup>
              
 //             </div>
 //             <div className="col-md-4">
 //               <div style={{ width: '900px' }}>
 //                 <FormGroupLabel>
 //                   Start Date<span style={{ color: 'red' }}>*</span>
 //                 </FormGroupLabel>
 //                 <FormGroupField>
 //                   <Field
 //                     style={{ width: '900px' }}
 //                     name="startDates"
 //                     render={({ input }) => (
 //                       <DatePicker
 //                         {...input}
 //                         selected={state.date.startDate}
 //                         // onChange={dates => setDate({ ...date, startDate: dates })}
 //                         dateFormat="dd-MM-yyyy"
 //                         readOnly
 //                         style={{ width: '900px' }}
 //                       />
                     
 //       )}
 //                   />
                   
 //                 </FormGroupField>
 //               </div>
 //             </div>
 
 //             <div className="col-md-4">
 //               <FormGroup>
 //                 <FormGroupLabel>
 //                   End Date<span style={{ color: 'red' }}>*</span>
 //                 </FormGroupLabel>
 //                 <FormGroupField>
 //                   <Field name="endDate">
 //                     {({ input }) => (
 //                       <DatePicker
 //                         {...input}
 //                         className="form-control"
 //                         selected={state.date.endDate}
 //                 //         onChange={dates => setDate({
 //                 //   ...date,
 //                 //   endDate: dates,
 //                 // })}
 //                         dateFormat="dd-MM-yyyy"
 //                         readOnly
 //                       />
 //             )}
 //                   </Field>
 //                 </FormGroupField>
 //               </FormGroup>
 //             </div>
 //             <div className="row">
 //               <div className="col-md-4">
 //                 <FormGroup>
 //                   <FormGroupLabel>Meal Name</FormGroupLabel>
 //                   <FormGroupField>
 //                     <Field 
 //                       name="mealName"
 //                       component="input"
 //                       type="text"
 //                       placeholder="Meal Name"
 //                       readOnly
 //                     />
 //                   </FormGroupField>
 //                 </FormGroup>
 //               </div>
 //               <div className="col-md-4">
 //                 <FormGroup>
 //                   <FormGroupLabel>Restaurant Name</FormGroupLabel>
 //                   <FormGroupField>
 //                     <Field name="resName" component="input" type="text" placeholder="Restaurant Name" readOnly />
 //                   </FormGroupField>
 //                 </FormGroup>
 //               </div>
 //               <div className="col-md-4">
 //                 <FormGroup>
 //                   <FormGroupLabel>Restaurant Location</FormGroupLabel>
 //                   <FormGroupField>
 //                     <Field
 //                       name="resLocation"
 //                       render={({ input, meta }) => (
 //                         <Select
 //                           {...input}
 //                           name="resLocation"
 //                           className="react-select w-100"
 //                           classNamePrefix="react-select"
 //                           options={restaurantLocationsOptions}
 //                           isClearable={false}
 //                           value={input.value || null}
 //                           onChange={(selectedOption) => {
 //             input.onChange(selectedOption); // Sync form state
 //             handleFieldChange('resLocation', selectedOption); // Update reInitialValue
 //           }}
 //                         />
 //       )}
 //                     />
 //                   </FormGroupField>
 //                 </FormGroup>
 //               </div>
 //               <div className="row">
 //                 <div className="col-md-4">
 //                   <FormGroup>
 //                     <FormGroupLabel>Meal Date</FormGroupLabel>
 //                     <FormGroupField>
 //                       <Field
 //                         name="bookingDate"
 //                         className="w-100"
 //                         render={({ input }) => (
 //                           <DatePicker
 //                             {...input}
 //                             value={state.mealDate}
 //                             selected={state.mealDate || null} // Ensure the selected date is null if not set
 //                             minDate={new Date(state.date.startDate)}
 //                             maxDate={new Date(state.date.endDate)}
 //                             onChange={(dates) => {
 //       input.onChange(dates); // Update form value
 //       handleMealDateChange(dates); // Call custom handler for additional actions
 //     }}
 //                             dateFormat="dd-MM-yyyy"
 //                             popperPlacement="top-start"
 //                             popperModifiers={{
 //       preventOverflow: {
 //         enabled: true,
 //         boundariesElement: 'viewport',
 //       },
 //     }}
 //                           />
 // )}
 //                       />
 
 //                     </FormGroupField>
 //                   </FormGroup>
 //                 </div>
 
 //                 <div className="col-md-4">
                   
 //                   <FormGroup>
 //                     <FormGroupLabel>Day Name</FormGroupLabel>
 //                     <FormGroupField>
 //                       <Field
 //                         name="dayname"
 //                         component="input"
 //                         type="text"
 //                         placeholder="Eg. Day 1"
 //                         onChange={(e) => {
 //                                //    form.change('dayname', e.target.value); // Sync the form state with the value
 //                                     //    setStep1formData(prevState => ({
 //                                     //      ...prevState,
 //                                     //      dayname: e.target.value, // Update the dayname in the state
 //                                     //    }));
 //                                      }}
 //                       />
 //                     </FormGroupField>
 //                   </FormGroup>
 //                 </div>
 //                 <div className="col-md-4">
 //                   <FormGroup>
 //                     <FormGroupLabel>Total Meals</FormGroupLabel>
 //                     <FormGroupField>
 //                       <Field
 //                         name="totalMeals"
 //                         render={({ input, meta }) => (
 //                           <input
 //                             {...input}
 //                             type="number"
 //                             placeholder="Total Meals"
 //                             value={state.totalMeals} // Controlled value
 //                             onChange={handleTotalMealsChange}
 //                           />
 //               )}
 //                       />
 //                     </FormGroupField>
 //                   </FormGroup>
 //                 </div>
 //               </div>
 //               <div className="row">
 //                 <div className="col-md-4">
 //                   <FormGroup>
 //                     <FormGroupLabel>Timing</FormGroupLabel>
 //                     <FormGroupField>
 //                       <Field
 //                         name="timing"
 //                         render={({ input, meta }) => (
 //                           <Select
 //                             {...input}
 //                             name="timing"
 //                             className="react-select w-100"
 //                             classNamePrefix="react-select"
 //                             options={filteredtimingOption}
 //                             isClearable={false}
 //                             value={input.value || null}
 //                             onChange={(selectedOption) => {
 //             input.onChange(selectedOption); // Sync form state
 //             handleFieldChange('timing', selectedOption); // Update reInitialValue
 //             handleBookingCheck(selectedOption);
 //           }}
 //                           />
 //       )}
 //                       />
 //                     </FormGroupField>
 //                   </FormGroup>
 //                 </div>
 //                 <div className="col-md-4">
 //                   <FormGroup>
 //                     <FormGroupLabel>Format</FormGroupLabel>
 //                     <FormGroupField>
 //                       <Field
 //                         name="format"
 //                         render={({ input, meta }) => (
 //                           <Select
 //                             {...input}
 //                             name="format"
 //                             className="react-select w-100"
 //                             classNamePrefix="react-select"
 //                             options={filteredtypeOption}
 //                             isClearable={false}
 //                             value={input.value || null}
 //                             onChange={(selectedOption) => {
 //               if (!selectedOption) {
 //                 console.error('Selected Option is undefined or null');
 //                 return;
 //               }
 //               console.log('Selected Option:', selectedOption); // Debug selectedOption
 //               input.onChange(selectedOption); // Sync form state
 //               handleFieldChange('format', selectedOption); // Update reInitialValue
 //             }}
 //                           />
 //         )}
 //                       />
 //                     </FormGroupField>
 //                   </FormGroup>
 //                 </div>
 //                 <div className="col-md-4">
 //                   <FormGroup>
 //                     <FormGroupLabel>Total Rate</FormGroupLabel>
 //                     <FormGroupField>
 //                       <h3 style={{
 //       fontSize: '24px', 
 //       fontWeight: 'bold', 
 //       color: '#007bff', 
 //       padding: '10px 20px', 
 //       backgroundColor: '#f1f1f1', 
 //       borderRadius: '8px', 
 //       textAlign: 'center', 
 //       boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', 
 //     }}
 //                       >
 //                         AED {state.mealTotalRate}
 //                       </h3>
 //                     </FormGroupField>
 //                   </FormGroup>
 //                 </div>
 
 //               </div>
               
 //             </div>
             
 //           </div>
           
 //           <div className="row">
 //             <div className="col-md-4">
 //               <div className="d-flex gap-3">
 //                 <Field
 //                   name="triptype"
 //                   component="input"
 //                   type="radio"
 //                   value="1"
 //                   disabled
 //                 />
 //                 <span>Group</span>
 //                 <Field name="triptype" component="input" type="radio" value="2" disabled />
 //                 <span>Family</span>
 //               </div>
 //             </div>
 //           </div>
 //           <div className="row">
 //             <div className="col-md-12 mt-3">
 //               <PaxDetailsTable 
 //                 paxDetails={state.paxData ? state.paxData : []}
 //                 selectedPaxs={selectedPaxs}
 //                 setSelectedPaxs={setSelectedPaxs}
 //                 triptype={state.reInitialValue.triptype}
 //               />
 //             </div>
 //           </div>
 //         </div>
         
 //         <br />
 //         <FormButtonToolbar>
 //           <Button variant="primary" type="submit">
 //             Book Meal 
 //           </Button>
 //         </FormButtonToolbar>
 //       </FormContainer>
 //     )}
 //   </Form>
 // );
 // }; 
 
 
 // BookingForm.propTypes = {
 //   mealName: PropTypes.string.isRequired,
 //   resName: PropTypes.string.isRequired,
 //   price: PropTypes.string.isRequired,
 //   id: PropTypes.string.isRequired,
 //   mealOffereds: PropTypes.string.isRequired,
 //   format: PropTypes.string.isRequired,
 //   locations: PropTypes.shape({
 //       id: PropTypes.string.isRequired,
 //       restaurant_name: PropTypes.string.isRequired,
   
 //     }).isRequired,
 // setmodalShowBooking: PropTypes.func.isRequired,
 // };
 
 // export default BookingForm;


